import { ArrowDownward } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { isNil } from "lodash";
import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import AmendTimesheetDialog from "../components/AmendTimesheetDialog";
import MobileTimesheetAccordion from "../components/MobileTimesheetAccordion";
import PageLayout from "../components/PageLayout";
import TimesheetAlert from "../components/TimesheetAlert";
import TimesheetTableAll from "../components/TimesheetTableAll";
import { TimeSheet } from "../types/timesheet";

interface Props {}

const Home: FunctionComponent<Props> = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const [selectedTimeSheet, setSelectedTimeSheet] = useState<TimeSheet>();
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [totalCount, setTotalCount] = useState<number>(0);
  const [recentExpanded, setRecentExpanded] = useState<boolean>(false);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <PageLayout header={t("timesheets.title")}>
      {isSmallScreen ? (
        <>
          {alertOpen && (
            <TimesheetAlert
              message={alertMessage}
              timer={5000}
              onTimerEnd={() => setAlertOpen(false)}
              onClose={() => setAlertOpen(false)}
            />
          )}
          <MobileTimesheetAccordion
            onTimeSheetAmend={(timesheet: TimeSheet) =>
              setSelectedTimeSheet(timesheet)
            }
            type="outstanding"
            setAlertOpen={setAlertOpen}
            setAlertMessage={setAlertMessage}
          />
        </>
      ) : (
        <>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ArrowDownward />}>
              <Stack direction={"row"} spacing={2}>
                <Typography> {t("current.outstanding")}</Typography>
                <Chip
                  label={`${totalCount} ${t("current.outstanding2")}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {alertOpen && (
                <TimesheetAlert
                  message={alertMessage}
                  timer={5000}
                  onTimerEnd={() => setAlertOpen(false)}
                  onClose={() => setAlertOpen(false)}
                />
              )}
              {isMediumScreen ? (
                <MobileTimesheetAccordion
                  onTimeSheetAmend={(timesheet: TimeSheet) =>
                    setSelectedTimeSheet(timesheet)
                  }
                  type="outstanding"
                  setAlertOpen={setAlertOpen}
                  setAlertMessage={setAlertMessage}
                  onDataLoaded={(total) => setTotalCount(total)}
                />
              ) : (
                <>
                  <TimesheetTableAll
                    onTimeSheetAmend={(timesheet) =>
                      setSelectedTimeSheet(timesheet)
                    }
                    type="outstanding"
                    setAlertOpen={setAlertOpen}
                    setAlertMessage={setAlertMessage}
                    onDataLoaded={(total) => setTotalCount(total)}
                    isEnabled={true}
                  />
                </>
              )}
            </AccordionDetails>
          </Accordion>

          <Accordion
            onChange={(_event, expanded) => setRecentExpanded(expanded)}
          >
            <AccordionSummary expandIcon={<ArrowDownward />}>
              <Typography> {t("current.recentlyApproved")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {isMediumScreen ? (
                <MobileTimesheetAccordion
                  onTimeSheetAmend={(timesheet: TimeSheet) =>
                    setSelectedTimeSheet(timesheet)
                  }
                  type="recently approved"
                  setAlertOpen={setAlertOpen}
                  setAlertMessage={setAlertMessage}
                />
              ) : (
                <TimesheetTableAll
                  onTimeSheetAmend={(timesheet) =>
                    setSelectedTimeSheet(timesheet)
                  }
                  type="recently approved"
                  setAlertOpen={setAlertOpen}
                  setAlertMessage={setAlertMessage}
                  isEnabled={recentExpanded}
                />
              )}
            </AccordionDetails>
          </Accordion>
        </>
      )}

      {selectedTimeSheet && (
        <AmendTimesheetDialog
          amendOpen={!isNil(selectedTimeSheet)}
          timesheet={selectedTimeSheet}
          handleAmendClose={() => setSelectedTimeSheet(undefined)}
          onAmendSuccess={() => {
            setSelectedTimeSheet(undefined);
            setAlertOpen(true);
            setAlertMessage(t("alerts.amendTimesheet"));
          }}
        />
      )}
      {!isSmallScreen && (
        <>
          <Typography variant="body2" sx={{ pt: "20px" }}>
            * {t("timesheets.weekEndingText")}
          </Typography>
        </>
      )}
    </PageLayout>
  );
};

export default Home;
